import { Appointment, AppointmentStatus, StatsDocNumbers, emptyStatsAccumulator, classifyStatus } from '../types';

export const toYyyyMmDdFromMmDdYyyy = (mmddyyyy: string): string => {
  if (!mmddyyyy) return '';
  const sanitized = mmddyyyy.trim();
  if (sanitized.length === 8) {
    return `${sanitized.slice(4, 8)}${sanitized.slice(0, 2)}${sanitized.slice(2, 4)}`;
  }
  return '';
};

export const toYyyyMmFromYyyyMmDd = (yyyymmdd: string): string => {
  if (!yyyymmdd) return '';
  const sanitized = yyyymmdd.trim();
  if (sanitized.length >= 6) {
    return sanitized.slice(0, 6);
  }
  return '';
};

export const applyContribution = (
  acc: StatsDocNumbers,
  appointment: Appointment,
  sign: number = 1
) => {
  const bucket = classifyStatus(appointment.status as AppointmentStatus);
  if (bucket === 'ignore') {
    return;
  }
  const amountCents = Math.round((appointment.billingAmount ?? 0) * 100) * sign;
  const minutes = Math.round(appointment.appointmentDuration ?? 0) * sign;

  if (bucket === 'upcoming') {
    acc.upcomingCount += sign;
    acc.upcomingRevenueCents += amountCents;
    acc.upcomingMinutes += minutes;
    return;
  }
  if (bucket === 'completed') {
    acc.completedCount += sign;
    acc.completedRevenueCents += amountCents;
    acc.completedMinutes += minutes;
    return;
  }
  if (bucket === 'canceled') {
    acc.canceledCount += sign;
    acc.canceledRevenueCents += amountCents;
    acc.canceledMinutes += minutes;
  }
};

export const buildStatsMapsFromAppointments = (appointments: Appointment[]) => {
  const businessDailyMap: Record<string, StatsDocNumbers> = {};
  const businessMonthlyMap: Record<string, StatsDocNumbers> = {};
  const employeeDailyMap: Record<string, Record<string, StatsDocNumbers>> = {};
  const employeeMonthlyMap: Record<string, Record<string, StatsDocNumbers>> = {};

  appointments.forEach((appointment) => {
    const bucket = classifyStatus(appointment.status as AppointmentStatus);
    if (bucket === 'ignore') {
      return;
    }
    const dayKey = toYyyyMmDdFromMmDdYyyy(appointment.appointmentDate);
    if (!dayKey) {
      return;
    }
    const monthKey = toYyyyMmFromYyyyMmDd(dayKey);
    const employeeId = appointment.employeeID;

    const businessDaily = businessDailyMap[dayKey] ?? (businessDailyMap[dayKey] = emptyStatsAccumulator());
    const businessMonthly = businessMonthlyMap[monthKey] ?? (businessMonthlyMap[monthKey] = emptyStatsAccumulator());
    applyContribution(businessDaily, appointment, 1);
    applyContribution(businessMonthly, appointment, 1);

    if (employeeId) {
      const dailyMap = employeeDailyMap[employeeId] ?? (employeeDailyMap[employeeId] = {});
      const monthlyMap = employeeMonthlyMap[employeeId] ?? (employeeMonthlyMap[employeeId] = {});
      const employeeDaily = dailyMap[dayKey] ?? (dailyMap[dayKey] = emptyStatsAccumulator());
      const employeeMonthly = monthlyMap[monthKey] ?? (monthlyMap[monthKey] = emptyStatsAccumulator());
      applyContribution(employeeDaily, appointment, 1);
      applyContribution(employeeMonthly, appointment, 1);
    }
  });

  return {
    businessDailyMap,
    businessMonthlyMap,
    employeeDailyMap,
    employeeMonthlyMap,
  };
};

export const normalizeStatsNumbers = (partial: Partial<StatsDocNumbers> | undefined): StatsDocNumbers => ({
  upcomingCount: partial?.upcomingCount ?? 0,
  completedCount: partial?.completedCount ?? 0,
  canceledCount: partial?.canceledCount ?? 0,
  upcomingRevenueCents: partial?.upcomingRevenueCents ?? 0,
  completedRevenueCents: partial?.completedRevenueCents ?? 0,
  canceledRevenueCents: partial?.canceledRevenueCents ?? 0,
  upcomingMinutes: partial?.upcomingMinutes ?? 0,
  completedMinutes: partial?.completedMinutes ?? 0,
  canceledMinutes: partial?.canceledMinutes ?? 0,
});

export const mergeStatsNumbers = (target: StatsDocNumbers, addition: StatsDocNumbers) => {
  target.upcomingCount += addition.upcomingCount;
  target.completedCount += addition.completedCount;
  target.canceledCount += addition.canceledCount;
  target.upcomingRevenueCents += addition.upcomingRevenueCents;
  target.completedRevenueCents += addition.completedRevenueCents;
  target.canceledRevenueCents += addition.canceledRevenueCents;
  target.upcomingMinutes += addition.upcomingMinutes;
  target.completedMinutes += addition.completedMinutes;
  target.canceledMinutes += addition.canceledMinutes;
};
